import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { clearVendorInvoiceStep } from '../contexts/useVendorInvoice'
import { axiosInstance, LocalStorage } from '../helpers'
import { useAppDispatch, useChannelList, useCompany } from '../hooks'
import { Company, User, WorkSpaceUser } from '../models'
import {
    login,
    setRootUser,
    getRootUser,
    setWorkSpaceUser,
    getInfrastructure,
    updateUser,
    createUser,
    LoginRequest,
    GetUserListRequest,
    CreateUserRequest,
    UpdateUserRequest,
    UpdateUserRequestFormData,
    BaseUserListThunk,
    UserDetailActionThunk,
    LoginActionThunk,
    RootState,
    getUserList,
} from '../store'
import { toast } from 'react-toastify'

// Only run auth once per page load so getRootUser isn't called repeatedly (Strict Mode double-mount, etc.)
let authRunThisPageLoad = false

interface UseUserOptions {
    shouldAuthenticate?: boolean
    shouldSetup?: boolean
}

interface UseUserReturn {
    rootUser?: User
    workspaceUser?: WorkSpaceUser
    userList: User[]
    isLoading: boolean
    loading: {
        organizationCompanyList: boolean
    }
    isError: boolean
    organization: number | null
    organizationCompanyList: Company[]
    fullUserList: User[]
    actions: {
        login: (req: LoginRequest) => LoginActionThunk
        logout: () => void
        loginWithToken: (token: string) => Promise<void>
        saveUserToState: (user: User) => void
        getUserList: (req: GetUserListRequest) => BaseUserListThunk
        createUser: (req: CreateUserRequest) => UserDetailActionThunk
        updateUser: (
            req: UpdateUserRequest | UpdateUserRequestFormData,
        ) => UserDetailActionThunk
        fetchOrganizationCompanyList: (
            organization: number | null,
            companyType?: CompanyType,
        ) => Promise<Company[] | Error | null>
        getFullUserList: (params?: GetFullUserListParams) => Promise<User[]>
    }
}

interface GetFullUserListParams {
    organization_id?: number
    company_id?: number
}

export const useUser = (options?: UseUserOptions): UseUserReturn => {
    const dispatch = useAppDispatch()
    const [loading, setLoading] = useState({
        organizationCompanyList: false,
    })
    const [isLoading, setIsLoading] = useState(true)
    const [isError, setIsError] = useState(false)

    const [fullUserList, setFullUserList] = useState<User[]>([])

    // Selectors
    const rootUser = useSelector((state: RootState) => state.user.rootUser)
    const workspaceUser = useSelector(
        (state: RootState) => state.user.workspaceUser,
    )
    const organization = useSelector(
        (state: RootState) => state.user.organization,
    )

    const [organizationCompanyList, setOrganizationCompanyList] = useState<
        Company[]
    >([])

    const userList = useSelector((state: RootState) => state.user.userList)
    const setupWorkspaceIdRef = useRef<number | null>(null)

    // Setup options
    const shouldAuthenticate = options?.shouldAuthenticate
    const shouldSetup =
        options?.shouldSetup &&
        !new URLSearchParams(location.search).get('setup')

    // Hooks
    const { getChannelList, getChannelDetail } = useChannelList()
    const { getApartmentList } = useCompany()

    const fetchOrganizationCompanyList = async (
        organization: number | null,
        companyType?: CompanyType,
    ) => {
        if (!organization) return null
        setLoading({ organizationCompanyList: true })
        const response = await axiosInstance
            .get(`organization/${organization}/company-list/`, {
                params: {
                    company_type: companyType,
                },
            })
            .catch((error) => {
                toast.error('Failed to fetch company list')
                return error
            })

        setLoading({ organizationCompanyList: false })

        if (response instanceof Error) {
            return response
        }
        setOrganizationCompanyList(response.data)
        return response.data
    }

    // Authentication handlers
    const handleAuthentication = async (token: string) => {
        try {
            setIsLoading(true)
            axiosInstance.defaults.headers.common[
                'Authorization'
            ] = `token ${token}`
            await dispatch(getRootUser({}))
            setIsError(false)
        } catch (error) {
            setIsError(true)
            handleLogout()
        } finally {
            setIsLoading(false)
        }
    }

    const handleLogout = () => {
        clearVendorInvoiceStep()
        dispatch(setRootUser(undefined))
        localStorage.removeItem(LocalStorage.EZTURN4_TOKEN)
        delete axiosInstance.defaults.headers.common['Authorization']
        // Redirect to login with ?logout=1 so the Login page signs out of Clerk. If we only reload(),
        // we end up on /login with Clerk still signed in and clerk_login runs again (user gets logged back in).
        const origin =
            typeof window !== 'undefined' ? window.location.origin : ''
        window.location.href = `${origin}/login?logout=1`
    }

    const handleLoginWithToken = async (token: string) => {
        try {
            const response = await axiosInstance.get('user/get_root_user/', {
                headers: { Authorization: `token ${token}` },
            })
            const rootUser = response.data
            dispatch(setRootUser(rootUser))
            dispatch(setWorkSpaceUser(rootUser.workspaces[0]))
        } catch (error) {
            handleLogout()
        }
    }

    // Setup handlers
    const setupWorkspace = async (workspaceUser: WorkSpaceUser) => {
        const { company_type, id } = workspaceUser.active_workspace

        if (company_type === 'APARTMENT') {
            await dispatch(getInfrastructure({ apartmentId: id }))
        } else if (company_type === 'VENDOR') {
            await getApartmentList({ params: {} })
        }

        const channelResponse = await getChannelList({
            params: { in_ezchat: true },
        })

        if (channelResponse.data.length > 0) {
            await getChannelDetail({ channelId: channelResponse.data[0].id })
        }
    }

    // Clear loading once we have user (handles Strict Mode double-mount where first mount's async setState is lost)
    useEffect(() => {
        if (rootUser?.workspaces?.length) {
            setIsLoading(false)
        }
    }, [rootUser])

    // Run auth only once per page load. Skip getRootUser if we already have rootUser (e.g. just finished regular login).
    useEffect(() => {
        if (!shouldAuthenticate) return
        const token = localStorage.getItem(LocalStorage.EZTURN4_TOKEN)
        if (!token) {
            setIsLoading(false)
            return
        }
        if (rootUser?.workspaces?.length) {
            setIsLoading(false)
            return
        }
        if (authRunThisPageLoad) return
        authRunThisPageLoad = true
        handleAuthentication(token)
    }, [shouldAuthenticate, rootUser])

    useEffect(() => {
        if (shouldAuthenticate && rootUser?.workspaces?.length) {
            const token = localStorage.getItem(LocalStorage.EZTURN4_TOKEN)
            const ws_user = token
                ? rootUser.workspaces.find((u) => u.token === token)
                : rootUser.workspaces[0]

            if (ws_user) dispatch(setWorkSpaceUser(ws_user))
        }
    }, [rootUser, shouldAuthenticate])

    // Run setup once per workspace so it doesn't loop (e.g. getChannelList -> state -> re-run)
    useEffect(() => {
        if (!workspaceUser || !shouldSetup) return
        const wsId = workspaceUser.id
        if (setupWorkspaceIdRef.current === wsId) return
        setupWorkspaceIdRef.current = wsId
        setupWorkspace(workspaceUser)
    }, [workspaceUser, shouldSetup])

    const getFullUserList = async (params?: GetFullUserListParams) => {
        try {
            const response = await axiosInstance.get('user/', {
                params: { all_users: true, ...params },
            })
            const fullUserList: User[] = response.data
            setFullUserList(fullUserList)
            return fullUserList
        } catch (error) {
            console.error(error)
            return Promise.reject(error)
        }
    }

    return {
        rootUser,
        workspaceUser,
        userList,
        isLoading,
        loading,
        isError,
        organization,
        organizationCompanyList,
        fullUserList,
        actions: {
            login: (req) => dispatch(login(req)),
            logout: handleLogout,
            loginWithToken: handleLoginWithToken,
            saveUserToState: (user) => dispatch(setRootUser(user)),
            getUserList: (req) => dispatch(getUserList(req)),
            createUser: (req) => dispatch(createUser(req)),
            updateUser: (req) => dispatch(updateUser(req)),
            fetchOrganizationCompanyList,
            getFullUserList,
        },
    }
}
